import { useEffect, useRef, useState } from 'react';
import { Paperclip, Mic, MicOff, X, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import limits from '../../../builderslab/shared/limits.json';
import PlanFirstToggle from '@/components/generation/PlanFirstToggle';
import { BTN_PRIMARY, BTN_QUIET } from '@/components/bento';
import { usePlanFirst } from '@/lib/buildPreferences';
import {
  readImageAttachment,
  speechRecognitionAvailable,
  startDictation,
  appendTranscript,
  dictationErrorMessage,
} from '@/lib/promptInput';

// ─── The prompt box ───────────────────────────────────────────────────────────
// One implementation, used by the marketing hero and by the dashboard's Home
// view. It was previously only in AIBuilderSection, so the dashboard had a plain
// textarea — no typed placeholder, no image, no dictation — and the two would
// have drifted the moment either changed.
//
// State that belongs to the box lives here (the typed placeholder, the
// attachment, the recogniser). The prompt itself is controlled by the parent,
// because both callers need to read it when they hand the build off.

// Read from builderslab/shared/limits.json, which the server's own cap reads
// too. The two used to be separate literals, and the home page carried a third
// — so raising the limit in one place left the box accepting more than the
// request would keep, and the extra was silently truncated server-side.
export const MAX_PROMPT = limits.promptChars;

/**
 * The counter's colour: yellow from 80% of the limit, red from 95%.
 *
 * These were the literals 1,200 and 1,425 — 80% and 95% of the old 1,500 cap —
 * and outlived the move to 5,000 in all three prompt boxes. A 1,425-character
 * brief showed a red "1425 / 5000", which reads as "you are at the limit", and
 * a customer cut a 3,000-character brief down to 100 words.
 */
export function promptCountTone(count) {
  if (count >= MAX_PROMPT * 0.95) return 'text-red-400';
  if (count >= MAX_PROMPT * 0.8) return 'text-yellow-400';
  return 'text-zinc-600';
}

const PLACEHOLDERS = [
  "What are we building today? e.g. 'A luxury real estate platform with high-end listings...'",
  'A sleek fintech banking app with dark mode...',
  'A vibrant food blog with recipe cards...',
  'A minimalist Web3 developer portfolio...',
];

/**
 * The placeholder that types itself.
 *
 * `reduceMotion` stops it dead rather than slowing it: for someone who has asked
 * for less movement, text that rewrites itself under the caret is the exact
 * thing they turned off. They get a readable static prompt instead.
 */
function useTypedPlaceholder(reduceMotion) {
  const [text, setText] = useState('');

  useEffect(() => {
    if (reduceMotion) { setText(PLACEHOLDERS[0]); return undefined; }

    let index = 0;
    let deleting = false;
    let current = '';
    let timeout;
    let delay = 500;

    const tick = () => {
      const full = PLACEHOLDERS[index];
      current = deleting
        ? full.substring(0, current.length - 1)
        : full.substring(0, current.length + 1);
      delay = deleting ? 20 : 40;
      setText(current);

      if (!deleting && current === full) {
        delay = 2500;
        deleting = true;
      } else if (deleting && current === '') {
        deleting = false;
        index = (index + 1) % PLACEHOLDERS.length;
        delay = 500;
      }
      timeout = setTimeout(tick, delay);
    };

    timeout = setTimeout(tick, delay);
    return () => clearTimeout(timeout);
  }, [reduceMotion]);

  return text;
}

export default function PromptComposer({
  value,
  onChange,
  onSubmit,                 // ({ prompt, image }) => void
  submitting = false,
  submitLabel = 'Build',
  submittingLabel = 'Checking…',
  footerNote = null,        // e.g. credits remaining
  reduceMotion = false,
  autoFocus = false,
  showPlanToggle = false,   // "Plan first" beside Build; its value rides on onSubmit
}) {
  const placeholder = useTypedPlaceholder(reduceMotion);
  const [planFirst, setPlanFirst] = usePlanFirst();
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedImage, setAttachedImage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // A recogniser left running after this unmounts keeps the mic light on.
  useEffect(() => () => {
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    // Reset straight away, so re-picking the same file still fires a change.
    e.target.value = '';
    if (!file) return;
    try {
      const { file: picked, image } = await readImageAttachment(file);
      setAttachedFile(picked);
      setAttachedImage(image);
      toast.success(`${picked.name} attached`);
    } catch (err) {
      setAttachedFile(null);
      setAttachedImage(null);
      toast.error(err.message);
    }
  };

  const clearAttachment = () => { setAttachedFile(null); setAttachedImage(null); };

  const handleVoiceRecord = () => {
    if (isRecording) { recognitionRef.current?.stop(); return; }
    if (!speechRecognitionAvailable()) {
      toast.error('Voice input is not supported in this browser.');
      return;
    }
    recognitionRef.current = startDictation({
      onTranscript: (t) => onChange(appendTranscript(value, t).slice(0, MAX_PROMPT)),
      onEnd: () => setIsRecording(false),
      onError: (event) => {
        setIsRecording(false);
        const message = dictationErrorMessage(event);
        if (message) toast.error(message);
      },
    });
    setIsRecording(true);
  };

  const submit = () => {
    if (!value.trim() || submitting) return;
    onSubmit({
      prompt: value.trim(),
      image: attachedImage,
      ...(showPlanToggle ? { planFirst } : {}),
    });
  };

  const count = value.length;

  return (
    // A size container: the row of buttons below is laid out by the box's own
    // width, not the screen's. See the comment on that row.
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-950 p-2 sm:p-3 focus-within:border-zinc-700 transition-colors [container-type:inline-size]">
      <Textarea
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value.slice(0, MAX_PROMPT))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
        }}
        placeholder={placeholder}
        aria-label="Describe your website"
        className="min-h-[96px] bg-transparent border-0 shadow-none text-white placeholder:text-zinc-500 text-base md:text-lg resize-none focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-2"
      />

      {count > 0 && (
        <div className="px-3 pb-1 flex justify-end">
          <span className={`text-xs tabular-nums font-medium transition-colors ${promptCountTone(count)}`}>
            {count} / {MAX_PROMPT}
          </span>
        </div>
      )}

      {/* Shown only once the file is genuinely readable and staged for the
          request, so the chip never over-promises. */}
      {attachedFile && (
        <div className="px-3 pb-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-800 text-xs text-zinc-300">
            <ImageIcon className="w-3 h-3 text-blue-400" />
            <span className="max-w-[180px] truncate">{attachedFile.name}</span>
            <span className="text-blue-400 text-[10px]">· vision</span>
            <button
              type="button"
              onClick={clearAttachment}
              aria-label={`Remove ${attachedFile.name}`}
              className="ml-0.5 text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {/* Laid out by the box's width, not the screen's: on a tablet the box is
          ~440px beside the dashboard's sidebar, and with labels switched on by
          the screen's width, "Upload Image", "Record", "Plan mode" and Build
          did not fit and drew over each other; on a 360px phone the switch
          covered the microphone. In a box under 560px the tools are icons
          with Plan mode beside them, and Build gets a row of its own, full
          width. From 560px up it is one row, labelled. The breakpoint is
          written out in every class because Tailwind only generates what it
          finds written out. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5 px-1 pt-2 border-t border-zinc-800 mt-2 relative z-10">
        <div className="flex items-center gap-1.5">
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          <Button
            variant="ghost" size="sm"
            onClick={() => fileInputRef.current?.click()}
            aria-label={attachedFile ? 'Change image' : 'Upload image'}
            title={attachedFile ? 'Change image' : 'Upload image'}
            className={`${BTN_QUIET} rounded-full h-9 w-9 px-0 [@container(min-width:560px)]:w-auto [@container(min-width:560px)]:px-3.5 text-sm`}
          >
            <Paperclip className="w-4 h-4 [@container(min-width:560px)]:mr-1.5" />
            <span className="hidden [@container(min-width:560px)]:inline">{attachedFile ? 'Change Image' : 'Upload Image'}</span>
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={handleVoiceRecord}
            aria-pressed={isRecording}
            aria-label={isRecording ? 'Stop recording' : 'Record'}
            title={isRecording ? 'Stop recording' : 'Record your idea'}
            className={`${BTN_QUIET} rounded-full h-9 w-9 px-0 [@container(min-width:560px)]:w-auto [@container(min-width:560px)]:px-3.5 text-sm ${isRecording ? 'bg-red-500/20 text-red-500 animate-pulse' : ''}`}
          >
            {isRecording
              ? <MicOff className="w-4 h-4 [@container(min-width:560px)]:mr-1.5" />
              : <Mic className="w-4 h-4 [@container(min-width:560px)]:mr-1.5" />}
            <span className="hidden [@container(min-width:560px)]:inline">{isRecording ? 'Listening…' : 'Record'}</span>
          </Button>
        </div>

        <div className="ml-auto flex items-center gap-3">
          {footerNote}
          {showPlanToggle && <PlanFirstToggle checked={planFirst} onCheckedChange={setPlanFirst} />}
        </div>

        <Button
          size="lg"
          onClick={submit}
          disabled={!value.trim() || submitting}
          className={`${BTN_PRIMARY} font-bold rounded-full h-11 w-full [@container(min-width:560px)]:h-10 [@container(min-width:560px)]:w-auto px-5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {submitting ? submittingLabel : submitLabel}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
